declare global {
  interface Window {
    FarcadeSDK: typeof sdk
    RemixSDK: typeof sdk
  }
}

export type ViewContext = 'feed' | 'full_screen' | 'challenge' | 'tournament'

export type SafeAreaInset = {
  top: number
  right: number
  bottom: number
  left: number
}

export const ZERO_SAFE_AREA_INSET: SafeAreaInset = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
}

export type GameState = Record<string, unknown>

export type Player = {
  id: string
  name: string
  purchasedItems: string[]
  imageUrl?: string
}

export type InventoryItem = {
  slug: string
  quantity: number
}

export type ShopItem = {
  slug: string
  name: string
  itemType?: string
  bitsCost?: number | null
  description?: string | null
  iconUrl?: string | null
  tier?: number | null
}

export type GameInfo = {
  players: Player[]
  player: Player
  shopItems?: ShopItem[]
  viewContext: ViewContext
  contentSafeAreaInset: SafeAreaInset
  initialGameState: {
    id: string
    gameState: GameState
  } | null
}

export type ReadyEvent = {
  type: 'ready'
  data: undefined
}

export type PlayAgainEvent = {
  type: 'play_again'
  data: undefined
}

export type SinglePlayerGameOverEvent = {
  type: 'game_over'
  data: {
    score: number
  }
}

export type MultiplayerGameOverEvent = {
  type: 'multiplayer_game_over'
  data: {
    scores: {
      playerId: string
      score: number
    }[]
  }
}

export type HapticFeedbackEvent = {
  type: 'haptic_feedback'
  data: undefined
}

export type ToggleMuteEvent = {
  type: 'toggle_mute'
  data: {
    isMuted: boolean
  }
}

export type GameErrorEvent = {
  type: 'error'
  data: {
    message: string
    source?: string
    lineno?: number
    colno?: number
    error?: Error
  }
}

export type GameInfoEvent = {
  type: 'game_info'
  data: GameInfo
}

export type MultiplayerSaveGameStateEvent = {
  type: 'multiplayer_save_game_state'
  data: {
    gameState: GameState
    alertUserIds?: string[]
  }
}

export type GameStateUpdatedEvent = {
  type: 'game_state_updated'
  data: {
    id: string
    gameState: GameState
  } | null
}

export type RefuteGameStateEvent = {
  type: 'refute_game_state'
  data: {
    gameStateId: string
  }
}

export type SaveGameStateEvent = {
  type: 'save_game_state'
  data: {
    gameState: GameState
  }
}

export type PurchaseEvent = {
  type: 'purchase'
  data: {
    item: string
  }
}

export type PurchaseCompleteEvent = {
  type: 'purchase_complete'
  data: {
    success: boolean
    item?: string
  }
}

export type GameEvent =
  | PlayAgainEvent
  | SinglePlayerGameOverEvent
  | ReadyEvent
  | HapticFeedbackEvent
  | ToggleMuteEvent
  | GameErrorEvent
  | SaveGameStateEvent
  | RefuteGameStateEvent
  | GameInfoEvent
  | GameStateUpdatedEvent
  | MultiplayerGameOverEvent
  | MultiplayerSaveGameStateEvent
  | PurchaseEvent
  | PurchaseCompleteEvent

export type GameEventMessage<T extends GameEvent['type']> = {
  type: 'game_event'
  event: Extract<GameEvent, { type: T }>
}

/**
 * Messages from the game host to the game client
 */
export type IncomingGameEvent = GameEventMessage<
  'play_again' | 'toggle_mute' | 'game_info' | 'game_state_updated' | 'purchase_complete'
>

export type OutgoingGameEvent = GameEventMessage<
  | 'game_over'
  | 'ready'
  | 'haptic_feedback'
  | 'error'
  | 'save_game_state'
  | 'refute_game_state'
  | 'multiplayer_game_over'
  | 'multiplayer_save_game_state'
  | 'purchase'
>

type EventCallback = (data: unknown) => void

export class RemixSDK {
  private isClient: boolean
  private target: Window | null = null
  private eventListeners: Map<string, Set<EventCallback>> = new Map()
  private readyPromiseResolve: ((gameInfo: GameInfo) => void) | null = null
  private purchasePromiseResolve: ((data: PurchaseCompleteEvent['data']) => void) | null = null
  private _gameInfo?: GameInfo
  private _gameState?: GameState | null

  constructor() {
    this.isClient = typeof window !== 'undefined'
    this.target = this.isClient ? window.parent : null

    if (this.isClient) {
      window.addEventListener('message', this.handleMessage)

      // Set up global error handler
      window.addEventListener('error', this.handleGlobalError)
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection)

      // send the ready message to the game host
      this.sendMessage('ready', undefined)
    }
  }

  on(eventType: IncomingGameEvent['event']['type'], callback: EventCallback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set())
    }
    this.eventListeners.get(eventType)?.add(callback)
  }

  off(eventType: IncomingGameEvent['event']['type'], callback: EventCallback) {
    this.eventListeners.get(eventType)?.delete(callback)
  }

  // explicitly named event listeners for better understanding and type safety
  onPlayAgain(callback: () => void) {
    this.on('play_again', () => callback())
  }

  onToggleMute(callback: (data: ToggleMuteEvent['data']) => void) {
    this.on('toggle_mute', (data) => callback(data as ToggleMuteEvent['data']))
  }

  onGameStateUpdated(callback: (data: GameStateUpdatedEvent['data']) => void) {
    this.on('game_state_updated', (data) => callback(data as GameStateUpdatedEvent['data']))
  }

  onGameInfo(callback: (data: GameInfoEvent['data']) => void) {
    this.on('game_info', (data) => callback(data as GameInfoEvent['data']))
  }

  onPurchaseComplete(callback: (data: PurchaseCompleteEvent['data']) => void) {
    this.on('purchase_complete', (data) => callback(data as PurchaseCompleteEvent['data']))
  }
  // end of explicitly named event listeners

  setTarget(target: Window) {
    this.target = target
  }

  get purchasedItems(): string[] {
    return this._gameInfo?.player.purchasedItems || []
  }

  get inventory(): InventoryItem[] {
    const counts = new Map<string, number>()
    for (const item of this.purchasedItems) {
      counts.set(item, (counts.get(item) || 0) + 1)
    }
    return [...counts.entries()].map(([slug, quantity]) => ({ slug, quantity }))
  }

  get shopItems(): ShopItem[] {
    return this._gameInfo?.shopItems || []
  }

  get gameInfo(): GameInfo | undefined {
    return this._gameInfo
  }

  get gameState(): GameState | null | undefined {
    return this._gameState
  }

  get players(): Player[] | undefined {
    return this._gameInfo?.players
  }

  get player(): Player | undefined {
    return this._gameInfo?.player
  }

  get isReady(): boolean {
    return !!this._gameInfo
  }

  ready = (): Promise<GameInfo> => {
    // if the game info is already set, return it immediately
    if (this._gameInfo) {
      return Promise.resolve(this._gameInfo)
    }

    // internal promise allows Remix to return GameInfo as a response to the ready message
    return new Promise((resolve) => {
      this.readyPromiseResolve = resolve
    })
  }

  purchase = (data: PurchaseEvent['data']): Promise<PurchaseCompleteEvent['data']> => {
    this.sendMessage('purchase', data)

    // internal promise allows Remix to return PurchaseCompleteEvent data as a response to the purchase message
    return new Promise((resolve) => {
      this.purchasePromiseResolve = resolve
    })
  }

  reportError = (data: GameErrorEvent['data']) => {
    this.sendMessage('error', data)
  }

  hapticFeedback = () => {
    this.sendMessage('haptic_feedback', undefined)
  }

  hasItem = (item: string): boolean => {
    return this.getItemPurchaseCount(item) > 0
  }

  getItemPurchaseCount = (item: string): number => {
    const inventoryItem = this.inventory.find((entry) => entry.slug === item)
    return inventoryItem?.quantity || 0
  }

  getShopItem = (slug: string): ShopItem | undefined => {
    return this.shopItems.find((item) => item.slug === slug)
  }

  singlePlayer = {
    actions: {
      ready: this.ready,
      hapticFeedback: this.hapticFeedback,
      reportError: this.reportError,
      purchase: this.purchase,
      gameOver: (data: SinglePlayerGameOverEvent['data']) => {
        this.sendMessage('game_over', data)
      },
      saveGameState: (data: SaveGameStateEvent['data']) => {
        this.sendMessage('save_game_state', data)
      },
    },
  }

  multiplayer = {
    actions: {
      ...this.singlePlayer.actions,
      gameOver: (data: MultiplayerGameOverEvent['data']) => {
        this.sendMessage('multiplayer_game_over', data)
      },
      refuteGameState: (data: RefuteGameStateEvent['data']) => {
        this.sendMessage('refute_game_state', data)
      },
      saveGameState: (data: MultiplayerSaveGameStateEvent['data']) => {
        this.sendMessage('multiplayer_save_game_state', data)
      },
    },
  }

  private emit(eventType: IncomingGameEvent['event']['type'], data: unknown) {
    // Handle game_info specially to resolve the ready promise
    if (eventType === 'game_info') {
      const eventData = data as GameInfoEvent['data']
      this._gameInfo = eventData

      // Set the game state if it exists and is not already set
      if (!this._gameState && eventData.initialGameState) {
        this._gameState = eventData.initialGameState.gameState
      }

      if (this.readyPromiseResolve) {
        this.readyPromiseResolve(this._gameInfo)
        this.readyPromiseResolve = null
      }
    }

    // Handle purchase_complete specially to resolve the purchase promise
    if (eventType === 'purchase_complete') {
      const eventData = data as PurchaseCompleteEvent['data']

      if (eventData.success && eventData.item && this._gameInfo?.player) {
        this.applyPurchaseToCurrentPlayer(eventData.item)
      }

      if (this.purchasePromiseResolve) {
        this.purchasePromiseResolve(eventData)
        this.purchasePromiseResolve = null
      }
    }

    if (eventType === 'game_state_updated') {
      const eventData = data as GameStateUpdatedEvent['data']
      if (eventData) {
        this._gameState = eventData.gameState
      } else {
        this._gameState = null
      }
    }

    for (const callback of this.eventListeners.get(eventType) || []) {
      callback(data)
    }
  }

  private handleMessage = (event: MessageEvent<IncomingGameEvent>) => {
    if (event.data?.type !== 'game_event') return

    this.emit(event.data.event.type, event.data.event.data)
  }

  private sendMessage = <T extends GameEvent['type']>(
    type: T,
    data: Extract<GameEvent, { type: T }>['data'],
  ) => {
    if (!this.isClient || !this.target) return
    const gameEvent = { type: 'game_event', event: { type, data } } as GameEventMessage<T>
    this.target.postMessage(gameEvent, '*')
  }

  private handleGlobalError = (event: globalThis.ErrorEvent) => {
    // Send both formats for backward compatibility
    this.sendMessage('error', {
      message: event.message || 'Unknown error',
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    })
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))

    // Send both formats for backward compatibility
    this.sendMessage('error', {
      message: error.message,
      error,
    })
  }

  private applyPurchaseToCurrentPlayer(item: string) {
    if (!this._gameInfo) return

    const currentPlayer = this._gameInfo.player

    currentPlayer.purchasedItems = [...currentPlayer.purchasedItems, item]

    this._gameInfo.players = this._gameInfo.players.map((player) => {
      if (player.id !== currentPlayer.id) return player
      return {
        ...player,
        purchasedItems: currentPlayer.purchasedItems,
      }
    })
  }
}

// Initialize and add to window
export const sdk = new RemixSDK()

// Add SDK to window object for easier access in HTML games
if (typeof window !== 'undefined') {
  window.FarcadeSDK = sdk
  window.RemixSDK = sdk
}
